import {
    Colors,
    Heading,
    Icon,
    Text,
    type IconSize,
    type IconName,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import React from 'react';

/**
 * Props for the InfoBadge component.
 *
 * @property icon - Name of the icon from the UI library to display.
 * @property primaryLabel - Main label text (e.g., category, type, profile).
 * @property secondaryLabel - Optional secondary label text (e.g., subtitle, type).
 * @property size - Size variant: 'sm', 'md', or 'lg'. Controls icon and text size.
 * @property className - Additional class names for custom styling.
 * @property ariaLabel - Accessible label for screen readers.
 * @property onClick - Optional callback when badge is clicked.
 */
export interface InfoBadgeProps {
    icon?: IconName;
    primaryLabel: string;
    secondaryLabel?: string;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    ariaLabel?: string;
    onClick?: () => void;
}

/**
 * Size configuration for InfoBadge variants.
 *
 * - iconContainer: Tailwind classes for icon container size
 * - iconSize: Icon size prop for UI library
 * - primary: Tailwind classes for primary label
 * - secondary: Tailwind classes for secondary label
 * - gap: Tailwind gap between icon and text
 *
 * The labels climb one ladder -- 12, 14, 16, 18, 20px -- read through the app's `--text-*` tokens
 * so the two variants cannot drift apart. Those tokens are declared in real pixels in `app.css`,
 * so the ladder renders the sizes it names.
 */
const sizeMap = {
    sm: {
        iconContainer: 'w-10 h-10 text-base',
        iconSize: 'lg' as IconSize,
        primary: 'text-body-sm! font-medium',
        secondary: 'text-caption!',
        gap: 'gap-2',
    },
    md: {
        iconContainer: 'w-16 h-16 text-lg',
        iconSize: 'xl' as IconSize,
        primary: 'text-body-lg font-semibold',
        secondary: 'text-body',
        gap: 'gap-3',
    },
    lg: {
        iconContainer: 'w-20 h-20 text-xl',
        iconSize: 44 as IconSize,
        primary: 'text-subtitle font-bold',
        secondary: 'text-body',
        gap: 'gap-4',
    },
};

/**
 * InfoBadge component displays a colored icon container with primary and optional secondary text labels.
 * Used for representing categories, types, profiles, statuses, or metadata in a compact, visually distinct format.
 *
 * @example
 * <InfoBadge icon="user" primaryLabel="Analyst" secondaryLabel="Profile" size="md" />
 */
export const InfoBadge: React.FC<InfoBadgeProps> = ({
    icon,
    primaryLabel,
    secondaryLabel,
    size = 'md',
    className,
    ariaLabel,
    onClick,
}) => {
    const sizeStyles = sizeMap[size];
    return (
        <div
            className={`flex max-w-full min-w-0 items-center ${sizeStyles.gap} rounded-lg ${className}`}
            aria-label={ariaLabel || primaryLabel}
            role={'group'}
            onClick={onClick}
        >
            {/* Icon container with background and icon */}
            {icon && (
                <div
                    className={`flex shrink-0 items-center justify-center rounded bg-purple-100 p-2 ${sizeStyles.iconContainer}`}
                    aria-hidden={'true'}
                >
                    <Icon
                        name={icon}
                        size={sizeStyles.iconSize}
                        color={Colors.SECONDARY_700}
                    />
                </div>
            )}

            {/* Text container with primary and optional secondary label. `min-w-0` lets the
                column shrink inside the badge so the labels can clip; the tooltip reveals the
                full text on hover. */}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span
                            className={
                                'flex min-w-0 flex-1 flex-col justify-center overflow-hidden'
                            }
                        >
                            <Heading
                                level={6}
                                className={sizeStyles.primary}
                                truncate
                            >
                                {primaryLabel}
                            </Heading>

                            {secondaryLabel && (
                                <Text
                                    className={sizeStyles.secondary}
                                    color={Colors.BASE_400}
                                    truncate
                                >
                                    {secondaryLabel}
                                </Text>
                            )}
                        </span>
                    </TooltipTrigger>

                    <TooltipContent
                        side={'top'}
                        className={'pointer-events-auto max-w-md'}
                    >
                        <p>{primaryLabel}</p>
                        {secondaryLabel && <p>{secondaryLabel}</p>}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
};
